<template>
    <div class="price-breakdown-container">
        <div v-for="row in rows" :key="row.id" :class="{ 'top-border': topBorder }">
            <RowGroup v-if="row.combine" :item="row" :mobile="true" />
            <Row v-else :item="row.items[0]" :mobile="forceMobile" />
        </div>

        <div class="price-breakdown-footer">
            <div>
                <Translation
                    v-if="isPaymentPage"
                    :translate="{ key: 'shared.total_to_pay' }" />
                <Translation v-else :translate="{ key: 'shared.total_price' }" />
            </div>
            <div class="pl-sm-3 pl-md-none total-price">
                {{ getMoneyObjectAsString(totalToPayNow) }}
            </div>
        </div>

        <TransitionExpand>
            <div v-if="isPaymentPage && isDownPayment && totalDownPayment">
                <Notification
                    icon="InfoCircle"
                    variant="neutral"
                    padding="l"
                    class="price-breakdown-reminder"
                    :tier="2"
                    ><div class="price-breakdown-reminder-content">
                        <span
                            >{{
                                t({
                                    key: 'payment.down_payment_due_date',
                                })
                            }}

                            <strong v-if="totalDownPayment.Date">
                                {{
                                    getLocaleDigitDateTime(
                                        convertToDate(totalDownPayment.Date)
                                    )
                                }}
                            </strong></span
                        >
                        <p class="price-breakdown-reminder-price">
                            {{ getMoneyObjectAsString(totalDownPayment.Price.Remaining) }}
                        </p>
                    </div>
                </Notification>
            </div>
        </TransitionExpand>

        <DiscountCode v-if="showDiscountCode" />
        <BookNowPayLater v-if="!isPaymentPage" />
    </div>
</template>

<script lang="ts">
    import { v4 as uuidv4 } from 'uuid';
    import { computed, watch, ref, defineComponent } from 'vue';
    import { DownPayment, PartPaymentMethodType } from '@/api/Cart/cart.interfaces';
    import { isWhiteLabel } from '@/helpers/url';
    import { ProductRow } from '../types';
    import { convertToDate } from '@shared/helpers/datetime/datetime';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { Money } from '@libs/money/money.interfaces';
    import { useStore } from '@/store';
    import { Page } from '@/store/general/types';
    import { getLocaleDigitDateTime } from '@shared/helpers/datetime/datetime.formatters';
    import { t } from '@shared/helpers/translation';
    import { showDiscountCodeField } from '@/api/Cart/cart.helpers';
    import {
        getProducts,
        getAmountAlreadyPaid,
        getDiscountCode,
        getGiftCards,
        getAgencyCommision,
        getPaymentDiscountMethod,
    } from '../map/index';
    import TransitionExpand from '@ui/components/TransitionExpand/TransitionExpand.vue';
    import Translation from '@ui/components/Translation/Translation.vue';
    import Row from './Row.vue';
    import RowGroup from './RowGroup.vue';
    import DiscountCode from './DiscountCode/DiscountCode.vue';
    import Notification from '@ui/components/Notification/Notification.vue';
    import BookNowPayLater from '../../BookNowPayLater/BookNowPayLater.vue';

    export default defineComponent({
        name: 'PriceBreakdown',
        components: {
            Row,
            RowGroup,
            DiscountCode,
            Notification,
            TransitionExpand,
            Translation,
            BookNowPayLater,
        },
        props: {
            combine: {
                type: Boolean,
                required: true,
            },
            hideDiscountCode: {
                type: Boolean,
                required: true,
            },
            forceMobile: {
                type: Boolean,
                default: false,
            },
            topBorder: {
                type: Boolean,
                default: false,
            },
        },

        methods: {
            t,
            getLocaleDigitDateTime,
            getMoneyObjectAsString,
            convertToDate,
        },

        setup(props) {
            const store = useStore();
            const rows = ref<ProductRow[]>([]);

            const isPaymentPage = store.state.general.page === Page.Payment;
            const total = computed(() => store.getters['cart/getTotalToPay']);

            const totalDownPayment = computed<DownPayment>(() => getTotalDownPayment());

            const products = computed(() => store.getters['cart/getProducts']);

            const showDiscountCode = computed(
                () => !props.hideDiscountCode && showDiscountCodeField(store.state.cart.data)
            );

            const isDownPayment = computed(
                () =>
                    store.getters['cart/getSelectedPartPaymentOption'] ===
                    PartPaymentMethodType.DownPayment
            );

            const totalToPayNow = computed<Money>(() => {
                if (isPaymentPage && isDownPayment) {
                    return totalDownPayment.value
                        ? totalDownPayment.value.Price.Now
                        : total.value;
                }

                return total.value;
            });

            function getTotalDownPayment(): DownPayment {
                return isDownPayment.value && store.getters['cart/getTotalDownPaymentToPay'];
            }

            const createRows = (): ProductRow[] => {
                const rows = [
                    ...getProducts(props.combine),
                    ...getGiftCards(),
                    ...getAmountAlreadyPaid(),
                    ...getAgencyCommision(),
                    ...getDiscountCode(isWhiteLabel()),
                    ...getPaymentDiscountMethod(),
                ];

                return rows.map((x) => ({ ...x, id: uuidv4() }));
            };

            watch(
                () => [total.value.Amount, products.value?.length],
                (newValue, oldValue) => {
                    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                        rows.value = createRows();
                    }
                },
                { immediate: true }
            );

            return {
                rows,
                total,
                totalDownPayment,
                totalToPayNow,
                isDownPayment,
                isPaymentPage,
                showDiscountCode,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'PriceBreakdown';
</style>
